import {
	loadScriptAsynchronously,
	MediaQueryKind,
	useConfiguredTheme,
	useSynchronizedRef,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useEffect,
	useMemo,
	useRef
} from "react";
import {
	CultureResolvableMap,
	resolveForCurrentCulture
} from "client_legacy/ndaw/culture";
import { MainMenuItem, useMainMenuStore } from "client/Features/Layout/mainMenuStore";
import { isNil, isNotNil } from "client/Misc/utils";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { Guid } from "client_legacy/ndaw/guid";
import {
	KellyConfiguration,
	userConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { createRoot } from "react-dom/client";
import { shallowComparison } from "client/Misc/compareUtils";
import styles from "./KellyWidget.module.less";

export type KellyWidgetProps = {
	kelly: KellyConfiguration;
	timeZoneIdentifier: string | null;
};

export const KellyWidget = (props: KellyWidgetProps): ReactElement | null => {
	const {
		kelly,
		timeZoneIdentifier
	} = props;

	const theme: Theme = useConfiguredTheme();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const scriptElement: RefObject<HTMLScriptElement | null> = useRef<HTMLScriptElement | null>(null);

	const {
		activeItem,
		activateItem,
		deactivateItem
	} = useMainMenuStore(
		x => ({
			activeItem: x.activeItem,
			activateItem: x.activateItem,
			deactivateItem: x.deactivateItem
		}),
		shallowComparison
	);

	const scriptUrl: string = useMemo(
		(): string => {
			const baseUrl: string = kelly.baseUrl.endsWith("/")
				? kelly.baseUrl
				: `${kelly.baseUrl}/`;

			return new URL("embed/widget.js", baseUrl).toString();
		},
		[kelly.baseUrl]
	);

	const widgetAttributes: WidgetAttributes = useMemo(
		(): WidgetAttributes => ({
			tenantId: kelly.tenantId,
			theme: themeToWidgetThemeMap[theme],
			locale: resolveForCurrentCulture(cultureToWidgetLocaleMap),
			timezone: timeZoneIdentifier,
			position: "right",
			...(isResponsive
				? responsiveViewAttributes
				: desktopViewAttributes
			)
		}),
		[
			isResponsive,
			kelly.tenantId,
			theme,
			timeZoneIdentifier
		]
	);

	const widgetAttributesRef: ReadonlyRefObject<WidgetAttributes> = useSynchronizedRef(widgetAttributes);

	useEffect(
		(): void => {
			const load = async (): Promise<void> => {
				try {
					scriptElement.current = await loadScriptAsynchronously(
						scriptUrl,
						widgetAttributesRef.current
					);
				} catch (e) {
					console.error("Failed to integrate with Kelly", e);
				}
			};

			load();
		},
		[scriptUrl, widgetAttributesRef]
	);

	useEffect(
		(): void => {
			if (isNil(scriptElement.current)) {
				return;
			}

			applyWidgetAttributes(scriptElement.current, widgetAttributes);
		},
		[widgetAttributes]
	);

	useEffect(
		(): EffectWithDestructorResult => subscribeToKellyWidgetOpenChange(
			(isOpen: boolean): void => {
				document.body
					.classList
					.toggle(styles.isOpen, isOpen);

				if (isOpen) {
					activateItem(MainMenuItem.ChatAssistant);

					return;
				}

				deactivateItem(MainMenuItem.ChatAssistant);
			}
		),
		[activateItem, deactivateItem]
	);

	useEffect(
		(): void => {
			if (activeItem === MainMenuItem.ChatAssistant || !isKellyWidgetOpen()) {
				return;
			}

			closeKellyWidget();
		},
		[activeItem]
	);

	return null;
};
KellyWidget.displayName = "KellyWidget";

export function initKellyWidget(): void {
	const {
		aiAssistant,
		timeZone
	} = userConfiguration();

	if (!aiAssistant.isAiAssistantAvailable) {
		return;
	}

	createRoot(document.createElement("div"))
		.render(
			<KellyWidget
				kelly={aiAssistant.kelly}
				timeZoneIdentifier={timeZone?.identifier ?? null}
			/>
		);
}

export function toggleKellyWidget(): void {
	withKellyWidget((widget: KellyWidgetInterop): void => widget.toggle());
}

export function closeKellyWidget(): void {
	withKellyWidget((widget: KellyWidgetInterop): void => widget.close());
}

export function isKellyWidgetOpen(): boolean {
	return window.KellyWidget?.isOpen ?? false;
}

export function subscribeToKellyWidgetOpenChange(
	callback: (isOpen: boolean) => void
): () => void {
	const openChangeHandler = (args: CustomEvent<{ isOpen: boolean; }>): void => callback(args.detail.isOpen);

	window.addEventListener("kelly.widget.open-change", openChangeHandler);

	return (): void => window.removeEventListener("kelly.widget.open-change", openChangeHandler);
}

function withKellyWidget(callback: KellyWidgetCallback): void {
	if (isNotNil(window.KellyWidget)) {
		callback(window.KellyWidget);

		return;
	}

	const hasPendingCallback: boolean = isNotNil(pendingCallback);

	pendingCallback = callback;

	if (hasPendingCallback) {
		return;
	}

	window.addEventListener(
		"kelly.widget.ready",
		(args: CustomEvent<{ widget: KellyWidgetInterop; }>): void => {
			const callbackToInvoke: KellyWidgetCallback | null = pendingCallback;

			pendingCallback = null;

			callbackToInvoke?.(args.detail.widget);
		},
		{ once: true }
	);
}

type KellyWidgetCallback = (widget: KellyWidgetInterop) => void;

let pendingCallback: KellyWidgetCallback | null = null;

type WidgetAttributes = Readonly<{
	tenantId: Guid;
	theme: WidgetTheme;
	locale: WidgetLocale;
	timezone: string | null;
	position: "left" | "right";
	displayMode: "floating" | "fullscreen";
	showClose: "true" | "false";
	showEnlarge: "true" | "false";
	showLauncher: "true" | "false";
}>;

type WidgetViewAttributes = Pick<
	WidgetAttributes,
	"displayMode" | "showClose" | "showEnlarge" | "showLauncher"
>;

type WidgetTheme = "light" | "dark" | "system";

type WidgetLocale = "en" | "nl";

function applyWidgetAttributes(
	scriptElement: HTMLScriptElement,
	attributes: WidgetAttributes
): void {
	for (const [name, value] of Object.entries(attributes)) {
		if (isNil(value)) {
			continue;
		}

		scriptElement.dataset[name] = value;
	}
}

const responsiveViewAttributes: WidgetViewAttributes = {
	displayMode: "fullscreen",
	showClose: "false",
	showEnlarge: "false",
	showLauncher: "false"
};

const desktopViewAttributes: WidgetViewAttributes = {
	displayMode: "floating",
	showClose: "true",
	showEnlarge: "true",
	showLauncher: "true"
};

const themeToWidgetThemeMap: { [key in Theme]: WidgetTheme; } = {
	[Theme.Light]: "light",
	[Theme.Dark]: "dark",
	[Theme.System]: "system"
};

const cultureToWidgetLocaleMap: CultureResolvableMap<WidgetLocale> = {
	"standard": "en",
	"nl": "nl"
};